<script lang="ts" setup>
import urlJoin from 'url-join'
import PreviewGallery from '~/components/Product/PreviewGallery.vue'
import Breadcrumbs from '~/components/Breadcrumbs.vue'
import { findProductVariant } from '~/utils/productHelpers'
import parseValidSlug from '~/utils/filters/parseValidSlug'
import type { ProductResponseInterface } from '~/types/market/productBackendTypes'
import Wrapper from '~/components/Page/Wrapper.vue'
import SaleBadge from '~/components/Product/SaleBadge.vue'
import Section from '~/components/Page/Section.vue'
import { useStoreConfig } from '~/store/storeConfig'
import type { Breadcrumb, ProductDetails } from '~/types/Product'
import Hook from '~/modules/WidgetsAndHooks/runtime/Hook.vue'
import HookNameEnum from '~/enums/widgets/HookNameEnum'
import { useLoaderStore } from '~/store/loader'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import QueryEnum from '~/enums/QueryEnum'
import { useAnalyticalDataTracking } from '~/composables/analytics/useAnalyticalDataTracking'
import getMarketRoute from '~/utils/getMarketRoute'
import { InteractionPlaceComponentNames } from '~/types/analytics/InteractionPlace'
import type ProductViewData from '~/types/analytics/marketplace/ProductViewData'
import scrollToTop from '~/utils/scrollToTop'
import getPublicUrl from '~/utils/routing/getPublicUrl'
import { useProductStore } from '~/store/product'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const router = useRouter()
const { t } = useLang()
const localeSetting = useState<string>('locale.setting')
const storeConfig = useStoreConfig()
const loaderStore = useLoaderStore()
const productStore = useProductStore()
const { isPerkuJavTheme, isWhitelabel, isTarpininke } = useTheme()
const redirect = ref<FetchProductResponse | null>(null)
const languageId = computed(
  () => storeConfig.getSite?.languages?.find((lang) => lang.iso_code.toLowerCase() === localeSetting.value)?.id
)

const variantId = router?.currentRoute.value?.query?.[QueryEnum.ProductVariant] as string
const slug = computed(() => urlJoin(...(router?.currentRoute.value?.params?.slug || [])))
const queryKey = ref<string>()
const searchText = ref<string>()
const componentHint = ref<InteractionPlaceComponentNames | undefined>()

const queryKeyComputed = computed(() => {
  const queryKeyValue = router?.currentRoute?.value?.query[QueryEnum.QueryKey] || null

  return queryKeyValue ? queryKeyValue.toString() : undefined
})

const searchTextComputed = computed(() => {
  const searchTextValue = router?.currentRoute?.value?.query[QueryEnum.SearchText] || null

  return searchTextValue ? searchTextValue.toString() : undefined
})

const componentHintComputed = computed(() => {
  let componentHintValue = router?.currentRoute?.value?.query[QueryEnum.ComponentHint] || null

  if (componentHintValue) {
    componentHintValue = [...componentHintValue].join('')
  }

  return componentHintValue &&
    Object.values(InteractionPlaceComponentNames).includes(componentHintValue as InteractionPlaceComponentNames)
    ? (componentHintValue as InteractionPlaceComponentNames)
    : undefined
})

const product = ref<ProductResponseInterface>()
const productVariant = ref<number>()
const productDetails = ref<ProductDetails>()
const inStock = computed(
  () =>
    !(
      productDetails.value?.available_quantity === 0 ||
      productDetails.value?.temporary_out_of_stock ||
      productDetails.value?.is_sold_out
    )
)

interface LastProductInterface {
  slug: string | null
  langId?: number | null
  response: ProductResponseInterface | null
  variantId: number | null
}

const lastProduct = ref<LastProductInterface>({
  slug: null,
  langId: null,
  response: null,
  variantId: null,
})

function buildProductViewEventData(): ProductViewData {
  const result: ProductViewData = {
    id: productVariant.value as number,
    category: (product.value as ProductResponseInterface).category as string,
    manufacturer: (product.value as ProductResponseInterface).manufacturer as string,
    name: (product.value as ProductResponseInterface).name as string,
    price: (product.value as ProductResponseInterface).current_price as number,
    sku: (product.value as ProductResponseInterface).SKU as string,
    variant: productVariant.value?.toString() ?? '',
    image: productDetails.value?.media[0] ?? '',
  }

  if (componentHint.value) {
    result.interaction_place = {
      componentName: componentHint.value,
      search: {
        queryKey: queryKey.value,
        queryText: searchText.value || '',
      },
    }
  }

  return result
}

function resetQueryParametersFromTracking() {
  const query: Record<string, any> = {}

  let removedKeysCount: number = 0

  Object.keys(router.currentRoute.value.query).forEach((key: string) => {
    const keysToRemove = [
      QueryEnum.QueryKey as string,
      QueryEnum.ComponentHint as string,
      QueryEnum.SearchText as string,
    ]

    if (keysToRemove.includes(key)) {
      removedKeysCount++

      return
    }

    query[key] = router.currentRoute.value.query[key]
  })

  if (removedKeysCount) {
    if (typeof window !== 'undefined') {
      let modifiedPath: string = `${router.currentRoute.value.path}`
      let queryString: string = ''

      if (Object.keys(query).length) {
        queryString = new URLSearchParams(query).toString()
        modifiedPath += `?${queryString}`
      }

      window.history.replaceState(null, '', modifiedPath)
    } else {
      navigateTo({
        query,
      })
    }
  }
}

function storeQueryParametersForTracking() {
  queryKey.value = queryKeyComputed.value
  searchText.value = searchTextComputed.value
  componentHint.value = componentHintComputed.value
}

type SlugType = string | null

interface FetchProductResponse {
  redirect: boolean
  slug: SlugType
}

async function fetchProductData(locale: string, slug: string): Promise<FetchProductResponse> {
  const redirect = {
    redirect: false,
    slug: null,
  }

  try {
    const newLanguageId = storeConfig.getSite?.languages?.find((lang) => lang.iso_code.toLowerCase() === locale)?.id

    const isDifferentProduct = slug && (lastProduct.value.langId !== newLanguageId || lastProduct.value.slug !== slug)

    if (isDifferentProduct) {
      const response = await $fetch<ProductResponseInterface>(`/api/market/product`, {
        method: 'POST',
        body: {
          slug,
          langId: newLanguageId,
        },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      if (lastProduct.value.slug) {
        await nextTick(() => {
          scrollToTop(true)
        })
      }

      lastProduct.value.slug = slug
      lastProduct.value.langId = newLanguageId
      lastProduct.value.response = response
    }

    if (lastProduct.value.response && lastProduct.value.response.id) {
      product.value = lastProduct.value.response
      productStore.setProduct(lastProduct.value.response)

      let itemId = variantId ? +variantId : product.value.item_id
      const route = router.currentRoute.value
      const itemIdFromUrl = Number(route.query[QueryEnum.ProductVariant] || 0)

      if (itemId && itemIdFromUrl && itemId !== itemIdFromUrl) {
        itemId = itemIdFromUrl
      }

      productVariant.value = itemId
      productStore.setVariant(productVariant.value)
      productDetails.value = findProductVariant(product.value, itemId)

      const previousVariantId = lastProduct.value.variantId

      lastProduct.value.variantId = productDetails.value?.item_id || null

      if (isDifferentProduct) {
        storeQueryParametersForTracking()
      }

      resetQueryParametersFromTracking()

      if (previousVariantId !== lastProduct.value.variantId) {
        await useAnalyticalDataTracking().trackMarketplaceProductView(buildProductViewEventData())
      }
    } else {
      return {
        redirect: true,
        slug,
      }
    }
  } catch (error) {
    return {
      redirect: true,
      slug,
    }
  }

  return redirect
}

function getRedirectSlug(slug: SlugType): SlugType {
  const validSlug = parseValidSlug(slug ?? '')

  if (validSlug === null) {
    return ''
  }

  if (slug && validSlug !== slug && router.currentRoute.value.meta.key === 'product') {
    return validSlug
  }

  return null
}

redirect.value = await fetchProductData(localeSetting.value, slug.value)

onMounted(() => {
  if (redirect.value?.redirect) {
    const path = getRedirectSlug(redirect.value?.slug)

    return navigateTo(buildCategoryUrl(path))
  }
})

let loaderTimeout: NodeJS.Timeout | string | number | undefined

watch(
  () => router.currentRoute.value.fullPath,
  async () => {
    if (router.currentRoute.value.meta.key === 'product') {
      clearTimeout(loaderTimeout)
      loaderStore.setLoader(true)
      redirect.value = await fetchProductData(localeSetting.value, slug.value)

      if (redirect.value?.redirect) {
        const path = getRedirectSlug(redirect.value?.slug)

        return navigateTo(buildCategoryUrl(path))
      } else {
        loaderTimeout = setTimeout(() => {
          loaderStore.setLoader(false)
        }, 300)
      }
    }
  }
)

watch(
  () => productVariant.value,
  (variant) => {
    productDetails.value = findProductVariant(product.value, variant)
  }
)

const onVariantChange = (variant: number) => {
  return navigateTo({
    query: {
      [QueryEnum.ProductVariant]: variant,
    },
  })
}

const productBreadcrumbs = computed(() =>
  product.value?.breadcrumb?.reduce(
    (prev: Breadcrumb[], next) => {
      return [...prev]?.concat({
        ...next,
        id: [prev?.[prev.length - 1].id, next.id].join('/'),
      })
    },
    [
      {
        id: null,
      },
    ]
  )
)

const metaTitle = computed(() =>
  `${productDetails.value?.manufacturer ?? ''} ${productDetails.value?.name ?? ''}`.trim()
)

const metaImage = computed(() => {
  return Array.isArray(productDetails.value?.media) ? productDetails.value?.media[0] : productDetails.value?.media
})

const route = useRoute()
const publicUrl = getPublicUrl()

const metaUrl = computed(() => {
  const url = urlJoin(publicUrl, route.path)

  if (route.query[QueryEnum.ProductVariant]) {
    return `${url}?${QueryEnum.ProductVariant}=${route.query[QueryEnum.ProductVariant]}`
  }

  return url
})

useHead(() => ({
  title: metaTitle.value,
  titleTemplate: (title) => `${title ?? ''} | ${storeConfig.getSite?.name}`,
  meta: [
    {
      name: 'og:title',
      content: metaTitle.value,
    },
    {
      name: 'og:url',
      content: metaUrl.value,
    },
    {
      name: 'og:image',
      content: metaImage.value,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: metaUrl.value,
    },
  ],
}))

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: productDetails.value?.name ?? '',
  image: productDetails.value?.media?.[0],
  description: product.value?.description,
  brand: {
    '@type': 'Brand',
    name: productDetails.value?.manufacturer,
  },
  offers: {
    '@type': 'Offer',
    url: metaUrl.value,
    priceCurrency: 'USD',
    price: productDetails.value?.current_price ?? product.value?.default_values?.current_price ?? 0,
    availability: inStock.value ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    itemCondition: 'https://schema.org/NewCondition',
  },
}))

const editLink = computed((): string | null => {
  return lastProduct.value.response?.editLink || null
})

const productBreadcrumb = computed(() => ({
  display_in_marketplace: 0,
  name: productDetails.value?.name,
  translation: productDetails.value?.name,
  id: urlJoin(localeSetting.value, 'p', slug.value, variantId ? `?productVariant=${variantId}` : ''),
}))

onUnmounted(() => {
  productStore.reset()
})

const texts = computed(() => {
  return {
    start: t('breadcrumb.start'),
    allProducts: t('common.all-products'),
  }
})

onBeforeUnmount(() => {
  if (loaderTimeout) {
    clearTimeout(loaderTimeout)
  }
})
</script>

<template>
  <div>
    <Wrapper v-if="slug && product && productDetails" class="product-component">
      <Section>
        <Breadcrumbs
          :not-active="true"
          :breadcrumbs="[
            { id: getMarketRoute(), translation: texts.start, isFullPath: true },
            { id: null, translation: texts.allProducts },
            ...(productBreadcrumbs?.filter((breadcrumb) => Boolean(breadcrumb.display_in_marketplace)) ?? []),
          ]"
          :product-breadcrumb="productBreadcrumb" />
      </Section>
      <Section>
        <div class="product-page">
          <section class="product-page-section">
            <div class="product-gallery">
              <SaleBadge :product-details="productDetails" />
              <PromoSticker :enabled="product.sticker_needed" :src="product.sticker" is-product-page />
              <PreviewGallery
                :media-gallery="productDetails.media"
                :product-variant="productVariant"
                :name="`${productDetails.manufacturer} ${productDetails.name}`" />
            </div>
            <div class="product-info">
              <LazyMainInfo
                :product="product"
                :product-details="productDetails"
                :product-variant="productVariant"
                :slug="product.slug"
                :query-key="queryKey"
                :search-text="searchText"
                :component-hint="componentHint"
                :in-stock="inStock"
                @on-variant-change="onVariantChange" />
            </div>
          </section>
        </div>
      </Section>
      <div class="separator" />
      <LazyAbout :product="product" />
      <LazyUniqueSellingPropositionsJav v-if="isPerkuJavTheme || isTarpininke" />
      <LazyUniqueSellingPropositionsWl v-else-if="isWhitelabel" />
      <LazyUniqueSellingPropositions v-else />
      <LazyRelatedProducts :products="product.similar_products" :language-id="languageId" />
      <Hook :name="HookNameEnum.ProductPage" />
    </Wrapper>

    <FloatingEditToggler :link="editLink" />
  </div>
</template>

<style lang="postcss" scoped>
.product-component {
  @apply mb-4;
}

.product-page {
  @apply relative pb-[3rem];

  .product-page-section {
    @apply grid grid-cols-1 lg:grid-cols-2 lg:gap-[2rem];

    .product-gallery {
      @apply relative;
      :deep(.sale-badge-component) {
        @apply left-4 md:left-0;
      }
    }

    .product-info {
      @apply relative;
    }
  }
}

.alt {
  .product-component {
    @apply !mb-0;
    .product-page {
      .product-page-section {
        @apply lg:grid-cols-3;
      }

      .product-info {
        @apply col-span-2;
      }
    }
  }
}

.nl {
  .product-component {
    @apply !mb-0;
    .product-page {
      .product-page-section {
        @apply lg:grid-cols-2;
      }

      .product-info {
        @apply col-span-1;
      }
    }
  }
}
</style>
